import { useState, useEffect } from 'react';

// Default templates that users can choose from
const DEFAULT_TEMPLATES = [
  {
    id: 'default-1',
    name: 'Standard Intro',
    template: 'Number {routineNumber}! A {ageGroup} {division} {category}! {routineName}!'
  },
  {
    id: 'default-2',
    name: 'Extended Intro',
    template: 'Coming to the stage, routine number {routineNumber}! This {ageGroup} {division} {category} is titled: {routineName}!'
  },
  {
    id: 'default-3',
    name: 'Welcome Format',
    template: 'Please welcome routine {routineNumber}! A {ageGroup} {division} {category}! {routineName}!'
  },
  {
    id: 'default-4',
    name: 'Performer Intro',
    template: 'Please welcome {performerName} performing routine number {routineNumber}, {routineName}!'
  },
  {
    id: 'default-5',
    name: 'Studio Introduction',
    template: 'From {studioCode}, please welcome routine number {routineNumber}, {routineName}!'
  }
];

/**
 * Custom hook for managing announcement templates
 * 
 * Allows the user to:
 * - Select from default templates
 * - Create custom templates
 * - Edit templates
 * - Delete templates
 * - Apply templates to routines
 */
export const useCustomAnnouncements = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Load templates from localStorage on mount
  useEffect(() => {
    const savedTemplates = localStorage.getItem('emceeTemplates');
    if (savedTemplates) {
      try {
        setTemplates(JSON.parse(savedTemplates));
      } catch (error) {
        console.error('Error loading templates:', error);
        // If there's an error parsing, just use the defaults
        setTemplates(DEFAULT_TEMPLATES);
      }
    } else {
      setTemplates(DEFAULT_TEMPLATES);
    }

    // Default to first template
    if (!selectedTemplate && DEFAULT_TEMPLATES.length > 0) {
      setSelectedTemplate(DEFAULT_TEMPLATES[0].id);
    }
  }, []);

  // Save templates to localStorage when they change
  useEffect(() => {
    if (templates.length > 0) {
      localStorage.setItem('emceeTemplates', JSON.stringify(templates));
    }
  }, [templates]);

  // Add a new template
  const addTemplate = (name, template) => {
    const newTemplate = {
      id: `custom-${Date.now()}`,
      name,
      template,
      isCustom: true
    };
    
    setTemplates([...templates, newTemplate]);
    return newTemplate;
  };

  // Edit an existing template
  const editTemplate = (id, name, template) => {
    const updatedTemplates = templates.map(t => {
      if (t.id === id) {
        return { ...t, name, template };
      }
      return t;
    });
    
    setTemplates(updatedTemplates);
  };

  // Delete a template
  const deleteTemplate = (id) => {
    // Only allow deletion of custom templates
    const template = templates.find(t => t.id === id);
    if (!template || !template.isCustom) return false;
    
    setTemplates(templates.filter(t => t.id !== id));
    
    // If the deleted template was selected, select the first available
    if (selectedTemplate === id && templates.length > 0) {
      setSelectedTemplate(templates[0].id);
    }
    
    return true;
  };

  // Apply a template to a routine
  const applyTemplate = (routine) => {
    if (!routine || !selectedTemplate) return '';
    
    const template = templates.find(t => t.id === selectedTemplate);
    if (!template) return '';
    
    // Replace template variables with routine values
    let announcement = template.template;
    
    // Define all possible replacements
    const replacements = {
      '{routineNumber}': routine.routineNumber || '',
      '{routineName}': routine.routineName || '',
      '{performerName}': routine.performerName || '',
      '{studioCode}': routine.studioCode || '',
      '{ageGroup}': routine.ageGroup || '',
      '{style}': routine.style || '',
      '{category}': routine.category || '',
      '{division}': routine.division || '',
      '{level}': routine.level || '',
    };
    
    // Apply all replacements
    Object.entries(replacements).forEach(([key, value]) => {
      announcement = announcement.replace(new RegExp(key, 'g'), value);
    });
    
    return announcement;
  };

  return {
    templates,
    selectedTemplate,
    setSelectedTemplate,
    addTemplate,
    editTemplate,
    deleteTemplate,
    applyTemplate
  };
};

export default useCustomAnnouncements; 